import { createContext, useContext, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { UserManager } from 'oidc-client-ts';
import { AuthProvider } from 'react-oidc-context';

import { TopologyContext, type NgTopologyConfiguration } from '@/app/topology';

import { WebUiProvider } from './WebUiProvider';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const status = (error as { status?: number } | null)?.status;
        if (status === 401 || status === 403) {
          return false;
        }
        return failureCount < 1;
      },
    },
  },
});

const UserManagerContext = createContext<UserManager | null>(null);

export function useUserManager(): UserManager {
  const userManager = useContext(UserManagerContext);
  if (!userManager) {
    throw new Error('useUserManager must be used within AppProviders');
  }

  return userManager;
}

type AppProvidersProps = {
  topology: NgTopologyConfiguration;
  userManager: UserManager;
  children: ReactNode;
};

export function AppProviders({ topology, userManager, children }: AppProvidersProps) {
  return (
    <TopologyContext.Provider value={topology}>
      <AuthProvider userManager={userManager} skipSigninCallback>
        <UserManagerContext.Provider value={userManager}>
          <QueryClientProvider client={queryClient}>
            <WebUiProvider>{children}</WebUiProvider>
          </QueryClientProvider>
        </UserManagerContext.Provider>
      </AuthProvider>
    </TopologyContext.Provider>
  );
}
